import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import toast from 'react-hot-toast';
import {IoMdArrowBack} from 'react-icons/io';
import {
  FaSearch,
  FaCalendarAlt,
  FaUniversity,
  FaUsers,
  FaClock,
  FaTrophy,
  FaEye,
  FaFilter,
  FaList,
  FaEdit,
  FaExclamationTriangle,
} from 'react-icons/fa';
import {
  useExportAssessmentsQuery,
  useGetAssessmentByIdQuery,
  useGetAssessmentCandidatesQuery,
  useGetAssessmentTestResultsQuery,
  useGetUsersByRoleQuery,
} from '../../api/assessment';
import Loader from '../../components/ui/Loader';
import {formatDateTime} from '../../utils/dateFormat';
import {pageRoutes} from '../../constants/pageRoutes';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import CodingReviewModal from '../../components/assessment/CodingReviewModal';
import TestQuestionsModal from '../../components/assessment/TestQuestionsModal';
import {exportCandidatesByAssessment} from '../../apis/apis';
import CreateInvigilatorModal from '../../components/CreateInvigilatorModal';
import useAuthStore from '../../store/useAuthStore';

const StatusBadge = ({status}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const InfoTag = ({icon: Icon, label}) => (
  <div className="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg">
    <Icon className="mr-2 w-4 h-4" />
    <span>{label}</span>
  </div>
);

const TestScoreBadge = ({score, total, type, subject}) => {
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;

  const getColorClass = () => {
    if (percentage >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (percentage >= 60)
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (percentage >= 40)
      return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div
      className={`px-2 py-1 rounded text-xs font-medium border ${getColorClass()}`}
    >
      <div className="font-semibold">
        {score}/{total}
      </div>
      <div className="text-xs opacity-75">{percentage}%</div>
    </div>
  );
};

// Enhanced component to handle both test sessions and coding sessions from the new API structure
const CandidateTestResults = ({
  assessmentId,
  candidateId,
  assessment,
  testId,
  setCodingReviewModal,
  candidate, // New prop to access candidate's sessions
}) => {
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);

  // Use the enhanced candidate data instead of separate API calls
  let testResult = null;
  let resultType = 'mcq';

  // Check if this is an MCQ test
  const mcqTest = assessment?.tests?.find((test) => test._id === testId);
  if (mcqTest && candidate?.testSessions) {
    const session = candidate.testSessions.find(
      (session) => session.testId === testId,
    );
    if (session) {
      testResult = {
        testId: testId,
        type: 'mcq',
        subject: session.testName,
        status: session.status,
        score: session.score,
        possibleScore: mcqTest.testQuestionCount,
        correctAnswers: session.score,
        resultPublished: assessment.resultPublished,
        isAutoSubmit: session.isAutoSubmit,
      };
    }
  }

  // Check if this is a coding problem (either from fixed assignment or candidate's assigned problems)
  if (!testResult && candidate?.codingSessions) {
    let actualTestId = testId;

    // Handle generic challenge mapping for random assignment
    if (
      testId.startsWith('coding_challenge_') ||
      testId.startsWith('query_challenge_')
    ) {
      // For generic challenges, we need to map to the candidate's actual assigned problem
      const isQueryChallenge = testId.startsWith('query_challenge_');
      const challengeIndex = parseInt(
        testId.replace(
          isQueryChallenge ? 'query_challenge_' : 'coding_challenge_',
          '',
        ),
      );

      // Filter coding sessions by challenge type and sort by assignedAt or problemId
      const filteredSessions = candidate.codingSessions
        .filter((session) => session.is_this_query === isQueryChallenge)
        .sort((a, b) => {
          // Sort by assignedAt if available, otherwise by problemId for consistency
          if (a.assignedAt && b.assignedAt) {
            return new Date(a.assignedAt) - new Date(b.assignedAt);
          }
          return a.problemId.localeCompare(b.problemId);
        });

      // Get the nth challenge (1-indexed)
      if (challengeIndex <= filteredSessions.length) {
        const targetSession = filteredSessions[challengeIndex - 1];
        actualTestId = targetSession.problemId;
      } else {
        // This generic challenge index doesn't exist for this candidate
        return <div className="text-gray-400 text-sm">Not assigned</div>;
      }
    }

    const codingSession = candidate.codingSessions.find(
      (session) => session.problemId === actualTestId,
    );
    if (codingSession) {
      resultType = 'coding';
      testResult = {
        testId: actualTestId,
        type: 'coding',
        subject: codingSession.problemName,
        status: codingSession.status,
        score: codingSession.score,
        possibleScore: codingSession.maxScore,
        correctAnswers: codingSession.score,
        resultPublished: assessment.resultPublished,
        is_this_query: codingSession.is_this_query,
        gradedAt: codingSession.gradedAt,
        isAutoSubmit: codingSession.isAutoSubmit,
      };
    }
  }

  if (!testResult) {
    return <div className="text-gray-400 text-sm">Not assigned</div>;
  }

  // For coding problems, show different states based on grading status
  if (testResult.type === 'coding') {
    const hasScore =
      testResult.score !== undefined && testResult.score !== null;
    const isGraded =
      testResult.gradedAt !== undefined && testResult.gradedAt !== null;
    const isPublished = testResult.resultPublished;

    if (testResult.status === 'not_started') {
      return <div className="text-gray-400 text-sm">Not started</div>;
    }

    if (testResult.status === 'submitted' && !isGraded) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
              Pending Review
            </div>
            {testResult.isAutoSubmit && (
              <FaExclamationTriangle
                className="text-orange-500 w-3 h-3"
                title="Auto-submitted (likely due to time expiry or tab switch)"
              />
            )}
          </div>
          <Button
            size="sm"
            variant="outlined"
            onClick={() =>
              setCodingReviewModal({
                isOpen: true,
                assessmentId: assessmentId,
                candidateId: candidateId,
                problemId: testResult.testId,
              })
            }
            className="text-xs px-1 py-1 h-auto"
            title="Grade Submission"
          >
            <FaEdit size={10} />
          </Button>
        </div>
      );
    }

    if (testResult.status === 'submitted' && isGraded) {
      const scoreDisplay = isPublished ? (
        <TestScoreBadge
          score={testResult.score}
          total={testResult.possibleScore}
          type={testResult.subject}
        />
      ) : (
        <div className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs border border-gray-300">
          <div className="font-semibold">
            {testResult.score}/{testResult.possibleScore}
          </div>
        </div>
      );

      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {scoreDisplay}
            {testResult.isAutoSubmit && (
              <FaExclamationTriangle
                className="text-orange-500 w-3 h-3"
                title="Auto-submitted (likely due to time expiry or tab switch)"
              />
            )}
          </div>
          <Button
            size="sm"
            variant="outlined"
            onClick={() =>
              setCodingReviewModal({
                isOpen: true,
                assessmentId: assessmentId,
                candidateId: candidateId,
                problemId: testResult.testId,
              })
            }
            className="text-xs px-1 py-1 h-auto"
            title="View/Edit Grade"
          >
            <FaEye size={10} />
          </Button>
        </div>
      );
    }

    if (testResult.status === 'in_progress') {
      return (
        <div className="flex items-center gap-2">
          <div className="text-yellow-600 text-sm">In Progress</div>
          <div className="text-yellow-600" title="In Progress">
            <FaClock size={12} />
          </div>
        </div>
      );
    }

    return <div className="text-gray-400 text-sm">Unknown Status</div>;
  }

  // For MCQ tests
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <TestScoreBadge
          score={testResult.correctAnswers}
          total={testResult.possibleScore}
          type={testResult.subject || testResult.type}
        />
        {testResult.isAutoSubmit && (
          <FaExclamationTriangle
            className="text-orange-500 w-3 h-3"
            title="Auto-submitted (likely due to time expiry or tab switch)"
          />
        )}
      </div>
      <Button
        size="sm"
        variant="outlined"
        onClick={() => setShowQuestionsModal(true)}
        className="text-xs px-1 py-1 h-auto"
        title="View Questions"
      >
        <FaList size={10} />
      </Button>
      <TestQuestionsModal
        isOpen={showQuestionsModal}
        onClose={() => setShowQuestionsModal(false)}
        assessmentId={assessmentId}
        candidateId={candidateId}
        testId={testId}
      />
    </div>
  );
};

const TotalScoreCell = ({assessmentId, candidateId, candidate, assessment}) => {
  // Calculate total score from candidate's actual sessions
  let totalScore = 0;
  let totalPossibleScore = 0;

  // Add MCQ test scores
  if (candidate?.testSessions) {
    candidate.testSessions.forEach((session) => {
      if (
        session.status === 'submitted' &&
        session.score !== null &&
        session.score !== undefined
      ) {
        totalScore += session.score || 0;
      }
      // Find the corresponding test to get max score
      const test = assessment?.tests?.find((t) => t._id === session.testId);
      if (test) {
        totalPossibleScore += test.testQuestionCount || 0;
      }
    });
  }

  // Add coding challenge scores
  if (candidate?.codingSessions) {
    candidate.codingSessions.forEach((session) => {
      if (
        session.status === 'submitted' &&
        session.score !== null &&
        session.score !== undefined
      ) {
        totalScore += session.score || 0;
      }
      totalPossibleScore += session.maxScore || 0;
    });
  }

  if (totalPossibleScore === 0) {
    return <div className="text-gray-400 text-sm">No score</div>;
  }

  return (
    <TestScoreBadge
      score={totalScore}
      total={totalPossibleScore}
      type="Total"
    />
  );
};

const AssessmentCandidates = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isInvigilator = user?.role === 'invigilator';
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [scoreFilters, setScoreFilters] = useState({});
  const [codingReviewModal, setCodingReviewModal] = useState({
    isOpen: false,
    assessmentId: null,
    candidateId: null,
    problemId: null,
  });
  const [isInvigilatorModalOpen, setIsInvigilatorModalOpen] = useState(false);
  const [isInvigilatorsModalOpen, setIsInvigilatorsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    page: 1,
    limit: 150,
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    scoreFilters: '{}',
  });

  const {
    data: assessmentData,
    isLoading: isLoadingAssessment,
    error: assessmentError,
  } = useGetAssessmentByIdQuery(id, {
    staleTime: 300000, // Cache for 5 minutes
    cacheTime: 300000,
  });

  const {
    data: candidatesData,
    isLoading: isLoadingCandidates,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useGetAssessmentCandidatesQuery(
    id,
    {
      page: formData.page,
      limit: formData.limit,
      search: formData.search,
      sortBy: formData.sortBy,
      sortOrder: formData.sortOrder,
      scoreFilters: formData.scoreFilters,
    },
    {
      enabled: !!assessmentData?.data,
      staleTime: 30000,
      cacheTime: 60000,
      keepPreviousData: true,
    },
  );

  const assessment = assessmentData?.data;
  const candidates = candidatesData?.data?.candidates || [];
  const totalItems = candidatesData?.data?.total || 0;
  const totalPages = candidatesData?.data?.totalPages || 0;
  const appliedFilters = candidatesData?.data?.appliedFilters || {};
  const assessmentInfo = candidatesData?.data?.assessmentInfo || {};
  const availableFilters = candidatesData?.data?.availableFilters || {
    tests: [],
    codingProblems: [],
  };

  // API call to fetch invigilators for this assessment
  const {
    data: invigilatorsData,
    isLoading: isLoadingInvigilators,
    refetch: refetchInvigilators,
  } = useGetUsersByRoleQuery('invigilator', id, {
    enabled: isInvigilatorsModalOpen, // Only fetch when modal is open
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setFormData((prev) => ({
        ...prev,
        search: searchTerm,
        scoreFilters: JSON.stringify(scoreFilters),
        page: 1, // Reset to first page on search/filter change
      }));
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm, scoreFilters]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prev) => ({
      ...prev,
      page: page || prev.page,
      sortBy: sortBy || prev.sortBy,
      sortOrder: sortDirection === 'asc' ? 'asc' : 'desc',
    }));
  };

  const handleCodingReviewSuccess = () => {
    // Refresh all results data by reloading the page
    // window.location.reload();
    refetchCandidates();
  };

  const handleScoreFilterChange = (testId, minScore) => {
    setScoreFilters((prev) => {
      const newFilters = {...prev};
      if (minScore === '' || minScore === null || minScore === undefined) {
        delete newFilters[testId];
      } else {
        newFilters[testId] = minScore;
      }
      return newFilters;
    });
  };

  const getActiveFilterCount = () => {
    if (scoreFilters.totalScorePercentage) {
      return 1; // Total score percentage filter
    }
    return Object.keys(scoreFilters).length;
  };

  const clearAllFilters = () => {
    setScoreFilters({});
  };

  // Calculate and apply percentage-based filters
  const applyPercentageFilter = (percentage) => {
    // Calculate total possible score
    let totalPossibleScore = 0;

    // Add MCQ test max scores
    availableFilters.tests?.forEach((test) => {
      totalPossibleScore += test.maxScore || 0;
    });

    // Add coding problem max scores
    availableFilters.codingProblems?.forEach((problem) => {
      totalPossibleScore += problem.maxScore || 0;
    });

    // Calculate minimum total score based on percentage
    const minTotalScore = Math.ceil((totalPossibleScore * percentage) / 100);

    // Set a special filter for total score percentage
    setScoreFilters({
      totalScorePercentage: {
        percentage,
        minTotalScore,
        totalPossibleScore,
      },
    });
  };

  if (isLoadingAssessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (assessmentError) {
    const errorMessage =
      assessmentError.response?.data?.message ||
      'Failed to fetch assessment details';
    toast.error(errorMessage);
    return (
      <div className="flex h-screen flex-col items-center justify-center p-4">
        <div className="rounded-lg bg-red-50 p-6 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto h-12 w-12 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-red-800">Error</h3>
          <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            className="mt-4"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-4">
        <div className="rounded-lg bg-yellow-50 p-6 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto h-12 w-12 text-yellow-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-yellow-800">
            Assessment Not Found
          </h3>
          <p className="mt-2 text-sm text-yellow-600">
            The assessment you&apos;re looking for doesn&apos;t exist or has
            been deleted.
          </p>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            className="mt-4"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const handleExportExcel = async () => {
    const res = await exportCandidatesByAssessment(id, {
      page: formData.page,
      limit: formData.limit,
      search: formData.search,
      sortBy: formData.sortBy,
      sortOrder: formData.sortOrder,
      scoreFilters: formData.scoreFilters,
    });
    if (res?.data?.fileUrl) {
      window.open(res?.data?.fileUrl, '_blank');
    }
  };

  return (
    <div className="px-8 py-6">
      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 4px;
          margin: 0 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          border: 1px solid #f8fafc;
          transition: background-color 0.2s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:active {
          background: #64748b;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        /* Scroll shadows */
        .scroll-shadow-left {
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 0) 100%
          );
          transition: opacity 0.3s ease;
        }
        .scroll-shadow-right {
          background: linear-gradient(
            to left,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 0) 100%
          );
          transition: opacity 0.3s ease;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">
            {assessment.name}
            {!!Number(totalItems) && (
              <span className="text-lg font-normal text-gray-600 ml-2">
                ({totalItems}{' '}
                {getActiveFilterCount() > 0 ? 'filtered' : 'total'} candidates)
              </span>
            )}
          </h1>
          <StatusBadge status={assessment.status} />
          {assessmentInfo.hasRandomAssignment && (
            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
              Random Assignment
            </span>
          )}
          {getActiveFilterCount() > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()} filters active
            </span>
          )}
        </div>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 gap-1.5"
        >
          <IoMdArrowBack size={18} />
          <span className="text-sm">Back to Assessments</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <InfoTag icon={FaUniversity} label={assessment.collegeName} />
        <InfoTag
          icon={FaCalendarAlt}
          label={`Start: ${assessment.startDateTimeFormatted}`}
        />
        <InfoTag
          icon={FaClock}
          label={`End: ${assessment.endDateTimeFormatted}`}
        />
        {assessment.tests?.length > 0 && (
          <InfoTag
            icon={FaTrophy}
            label={`MCQ Tests: ${assessment.tests.length}`}
          />
        )}
        {assessmentInfo.hasRandomAssignment ? (
          <InfoTag
            icon={FaTrophy}
            label={`Coding: ${assessmentInfo.totalChallenges} random (${assessmentInfo.queryCount} SQL, ${assessmentInfo.nonQueryCount} Programming)`}
          />
        ) : (
          availableFilters.codingProblems?.length > 0 && (
            <InfoTag
              icon={FaTrophy}
              label={`Coding Problems: ${availableFilters.codingProblems.length}`}
            />
          )
        )}
      </div>

      <form className="py-5">
        {/* Search Bar + Filters in Same Row */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex flex-col  gap-4 flex-1">
            {/* Search Input */}
            <div className="flex-1 max-w-md">
              <InputField
                type="text"
                placeholder="Search candidates"
                value={searchTerm}
                onChange={handleSearch}
                leftIcon={<FaSearch className="text-gray-400" />}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-4 flex-1">
              {/* Filter Chips */}
              {(assessment?.tests?.length > 0 ||
                availableFilters.codingProblems?.length > 0) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Percentage Suggestions */}
                  <div className="flex items-center gap-1 mr-3">
                    <span className="text-xs font-medium text-gray-600">
                      Quick:
                    </span>
                    {[40, 50, 60, 70].map((percentage) => (
                      <button
                        key={percentage}
                        type="button"
                        onClick={() => applyPercentageFilter(percentage)}
                        className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        title={`Filter candidates with ≥${percentage}% total score across all tests and coding challenges`}
                      >
                        {percentage}%
                      </button>
                    ))}
                  </div>

                  <span className="text-xs font-medium text-gray-600">
                    Filters:
                  </span>

                  {/* Total Score Percentage Filter */}
                  {scoreFilters.totalScorePercentage && (
                    <div className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-xs transition-all bg-purple-100 border-purple-300 text-purple-800">
                      <span className="font-medium">
                        Total Score ≥{' '}
                        {scoreFilters.totalScorePercentage.percentage}%
                      </span>
                      <span className="text-purple-600 text-xs">
                        ({scoreFilters.totalScorePercentage.minTotalScore}/
                        {scoreFilters.totalScorePercentage.totalPossibleScore})
                      </span>
                    </div>
                  )}

                  {/* MCQ Tests */}
                  {!scoreFilters.totalScorePercentage &&
                    (availableFilters.tests?.length > 0
                      ? availableFilters.tests
                      : assessment.tests || []
                    ).map((test) => (
                      <div
                        key={test._id}
                        className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-xs transition-all ${
                          scoreFilters[test._id]
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">{test.subject}</span>
                        <span className="text-gray-400">≥</span>
                        <input
                          type="number"
                          min="0"
                          max={test.maxScore || test.testQuestionCount}
                          step="1"
                          value={scoreFilters[test._id] || ''}
                          onChange={(e) =>
                            handleScoreFilterChange(test._id, e.target.value)
                          }
                          placeholder="0"
                          className={`w-12 text-center text-xs h-5 rounded border px-0.5 transition-colors ${
                            scoreFilters[test._id]
                              ? 'border-blue-300 bg-white text-blue-800 focus:border-blue-400'
                              : 'border-gray-300 bg-gray-50 text-gray-700 focus:border-blue-400 focus:bg-white'
                          }`}
                        />
                      </div>
                    ))}

                  {/* Coding Problems */}
                  {!scoreFilters.totalScorePercentage &&
                    availableFilters.codingProblems?.map((problem) => (
                      <div
                        key={problem._id}
                        className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-xs transition-all ${
                          scoreFilters[problem._id]
                            ? 'bg-green-100 border-green-300 text-green-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                        title={
                          assessmentInfo.hasRandomAssignment &&
                          problem.isGeneric
                            ? `Filter candidates who scored at least the specified amount in their assigned "${problem.name}"`
                            : assessmentInfo.hasRandomAssignment
                            ? `Filter candidates who were assigned "${problem.name}" and scored at least the specified amount`
                            : `Filter candidates who scored at least the specified amount in "${problem.name}"`
                        }
                      >
                        <span className="font-medium">
                          {problem.name}
                          {!problem.isGeneric && problem.is_this_query
                            ? ' (SQL)'
                            : ''}
                        </span>
                        <span className="text-gray-400">≥</span>
                        <input
                          type="number"
                          min="0"
                          max={problem.maxScore}
                          step={problem.maxScore > 1 ? '1' : '0.1'}
                          value={scoreFilters[problem._id] || ''}
                          onChange={(e) =>
                            handleScoreFilterChange(problem._id, e.target.value)
                          }
                          placeholder="0"
                          className={`w-12 text-center text-xs h-5 rounded border px-0.5 transition-colors ${
                            scoreFilters[problem._id]
                              ? 'border-green-300 bg-white text-green-800 focus:border-green-400'
                              : 'border-gray-300 bg-gray-50 text-gray-700 focus:border-green-400 focus:bg-white'
                          }`}
                        />
                      </div>
                    ))}

                  {/* Clear Filters Button */}
                  {getActiveFilterCount() > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {isLoadingCandidates && (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {getActiveFilterCount() > 0 ? 'Filtering...' : 'Loading...'}
            </div>
          )}
        </div>

        {/* Active Filter Summary */}
        {getActiveFilterCount() > 0 && (
          <div className="text-xs text-gray-600 mb-2">
            {scoreFilters.totalScorePercentage ? (
              <span>
                <span className="font-medium">Total Score Filter:</span> Showing
                candidates with ≥{scoreFilters.totalScorePercentage.percentage}%
                total score ({scoreFilters.totalScorePercentage.minTotalScore}/
                {scoreFilters.totalScorePercentage.totalPossibleScore} points)
              </span>
            ) : (
              <span>
                <span className="font-medium">{getActiveFilterCount()}</span>{' '}
                filter
                {getActiveFilterCount() > 1 ? 's' : ''} active • Showing
                candidates who meet ALL requirements
                {assessmentInfo.hasRandomAssignment && (
                  <span className="ml-2 text-blue-600">
                    (Only checking filters for problems assigned to each
                    candidate)
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </form>

      {/* Candidates Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        {/* Action Bar */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-gray-900">Candidates</h3>
            {totalItems > 0 && (
              <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                {totalItems} {getActiveFilterCount() > 0 ? 'filtered' : 'total'}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              onClick={() => setIsInvigilatorsModalOpen(true)}
            >
              <FaEye className="w-4 h-4" />
              View Invigilators
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              onClick={handleExportExcel}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 101.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 101.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 10-1.414-1.414L15 13.586V8z"
                  clipRule="evenodd"
                />
              </svg>
              Export Excel
            </button>
            {!isInvigilator && (
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                onClick={() => setIsInvigilatorModalOpen(true)}
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Create Invigilators
              </button>
            )}
          </div>
        </div>

        {/* Responsive Table Container */}
        <div className="overflow-hidden">
          {isLoadingCandidates ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-gray-500">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="font-medium">Loading candidates...</span>
              </div>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No candidates found
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {getActiveFilterCount() > 0
                  ? 'No candidates match your current filter criteria. Try adjusting the filters or clearing them to see all candidates.'
                  : 'No candidates have registered for this assessment yet.'}
              </p>
              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="relative">
              {/* Coding challenges scroll indicator */}
              {availableFilters.codingProblems?.length > 1 && (
                <div className="block lg:hidden text-center py-2 text-xs text-green-600 bg-green-50 border-b border-green-200">
                  <div className="flex items-center justify-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Scroll to view more coding challenges</span>
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              )}

              {/* Single Table with Scrollable Coding Challenges Section */}
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Table Header */}
                  <thead className="bg-gray-50">
                    <tr>
                      {/* Fixed Candidate Column */}
                      <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 shadow-sm">
                        <div className="flex items-center gap-2 min-w-[200px]">
                          <span>Candidate</span>
                          <button
                            onClick={() =>
                              handleSort({
                                sortBy: 'name',
                                sortDirection:
                                  formData.sortOrder === 'asc' ? 'desc' : 'asc',
                              })
                            }
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 101.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 101.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 10-1.414-1.414L15 13.586V8z" />
                            </svg>
                          </button>
                        </div>
                      </th>

                      {/* MCQ Test Columns - Fixed */}
                      {availableFilters.tests?.map((test) => (
                        <th
                          key={`mcq-${test._id}`}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50 border-r border-blue-100"
                        >
                          <div className="min-w-[120px]">
                            <div className="font-semibold text-blue-800">
                              {test.subject}
                            </div>
                            <div className="text-xs text-blue-600 font-normal">
                              MCQ Test
                            </div>
                          </div>
                        </th>
                      ))}

                      {/* Coding Problem Columns - Scrollable */}
                      {availableFilters.codingProblems
                        ?.sort((a, b) => {
                          // For generic challenges, sort by type first (coding challenges before query challenges)
                          // then by challenge index
                          if (a.isGeneric && b.isGeneric) {
                            if (a.challengeType !== b.challengeType) {
                              return a.challengeType === 'coding' ? -1 : 1;
                            }
                            return a.challengeIndex - b.challengeIndex;
                          }

                          // For non-generic challenges, use the original sorting logic
                          if (!a.isGeneric && !b.isGeneric) {
                            if (a.is_this_query === b.is_this_query) {
                              return a.name.localeCompare(b.name);
                            }
                            return a.is_this_query ? 1 : -1;
                          }

                          // Mix of generic and non-generic, put generic first
                          return a.isGeneric ? -1 : 1;
                        })
                        .map((problem) => (
                          <th
                            key={`coding-${problem._id}`}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50 border-r border-green-100"
                          >
                            <div className="min-w-[140px]">
                              <div className="font-semibold text-green-800">
                                {problem.name}
                              </div>
                              <div className="text-xs text-green-600 font-normal">
                                {problem.is_this_query
                                  ? 'SQL Challenge'
                                  : 'Programming Challenge'}
                              </div>
                            </div>
                          </th>
                        ))}

                      {/* Fixed Total Score Column */}
                      <th className="sticky right-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 shadow-sm">
                        <div className="min-w-[100px]">
                          <span>Total Score</span>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.map((candidate, index) => (
                      <tr
                        key={candidate._id}
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        {/* Fixed Candidate Info */}
                        <td className="sticky left-0 z-10 bg-inherit px-4 py-4 border-r border-gray-200 shadow-sm">
                          <div className="min-w-[200px]">
                            <div className="font-medium text-gray-900">
                              {candidate.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {candidate.email}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {candidate.enrollment && (
                                <span className="mr-3">
                                  ID: {candidate.enrollment}
                                </span>
                              )}
                              <span>{candidate.course}</span>
                            </div>
                          </div>
                        </td>

                        {/* MCQ Test Results - Fixed */}
                        {availableFilters.tests?.map((test) => (
                          <td
                            key={`mcq-${test._id}-${candidate._id}`}
                            className="px-4 py-4 bg-blue-50/30 border-r border-blue-100"
                          >
                            <div className="min-w-[120px]">
                              <CandidateTestResults
                                assessmentId={id}
                                candidateId={candidate._id}
                                assessment={assessment}
                                testId={test._id}
                                setCodingReviewModal={setCodingReviewModal}
                                candidate={candidate}
                              />
                            </div>
                          </td>
                        ))}

                        {/* Coding Problem Results - Scrollable */}
                        {availableFilters.codingProblems
                          ?.sort((a, b) => {
                            // For generic challenges, sort by type first (coding challenges before query challenges)
                            // then by challenge index
                            if (a.isGeneric && b.isGeneric) {
                              if (a.challengeType !== b.challengeType) {
                                return a.challengeType === 'coding' ? -1 : 1;
                              }
                              return a.challengeIndex - b.challengeIndex;
                            }

                            // For non-generic challenges, use the original sorting logic
                            if (!a.isGeneric && !b.isGeneric) {
                              if (a.is_this_query === b.is_this_query) {
                                return a.name.localeCompare(b.name);
                              }
                              return a.is_this_query ? 1 : -1;
                            }

                            // Mix of generic and non-generic, put generic first
                            return a.isGeneric ? -1 : 1;
                          })
                          .map((problem) => (
                            <td
                              key={`coding-${problem._id}-${candidate._id}`}
                              className="px-4 py-4 bg-green-50/30 border-r border-green-100"
                            >
                              <div className="min-w-[140px]">
                                <CandidateTestResults
                                  assessmentId={id}
                                  candidateId={candidate._id}
                                  assessment={assessment}
                                  testId={problem._id}
                                  setCodingReviewModal={setCodingReviewModal}
                                  candidate={candidate}
                                />
                              </div>
                            </td>
                          ))}

                        {/* Fixed Total Score */}
                        <td className="sticky right-0 z-10 bg-inherit px-4 py-4 border-l border-gray-200 shadow-sm">
                          <div className="min-w-[100px]">
                            <TotalScoreCell
                              assessmentId={id}
                              candidateId={candidate._id}
                              candidate={candidate}
                              assessment={assessment}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Showing {(formData.page - 1) * formData.limit + 1} to{' '}
                {Math.min(formData.page * formData.limit, totalItems)} of{' '}
                {totalItems} candidates
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  handleSort({page: Math.max(1, formData.page - 1)})
                }
                disabled={formData.page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                  const page =
                    Math.max(1, Math.min(totalPages - 4, formData.page - 2)) +
                    i;
                  return (
                    <button
                      key={page}
                      onClick={() => handleSort({page})}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === formData.page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() =>
                  handleSort({page: Math.min(totalPages, formData.page + 1)})
                }
                disabled={formData.page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Coding Review Modal */}
      <CodingReviewModal
        isOpen={codingReviewModal.isOpen}
        onClose={() =>
          setCodingReviewModal({
            isOpen: false,
            assessmentId: null,
            candidateId: null,
            problemId: null,
          })
        }
        assessmentId={codingReviewModal.assessmentId}
        candidateId={codingReviewModal.candidateId}
        problemId={codingReviewModal.problemId}
        onSuccess={handleCodingReviewSuccess}
      />
      <CreateInvigilatorModal
        isOpen={isInvigilatorModalOpen}
        onClose={() => setIsInvigilatorModalOpen(false)}
        assessmentId={id}
      />

      {/* View Invigilators Modal */}
      {isInvigilatorsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Invigilators
              </h2>
              <button
                onClick={() => setIsInvigilatorsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingInvigilators ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3 text-gray-500">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="font-medium">Loading invigilators...</span>
                  </div>
                </div>
              ) : invigilatorsData?.data?.userData?.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Total: {invigilatorsData.data.userData.length}{' '}
                    invigilator(s) assigned to this assessment
                  </p>
                  {invigilatorsData.data.userData.map((invigilator, index) => (
                    <div
                      key={invigilator._id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {`${invigilator.firstName || ''} ${
                              invigilator.lastName || ''
                            }`.trim() ||
                              invigilator.userName ||
                              'N/A'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {invigilator.email || 'No email available'}
                          </p>
                          {invigilator.phone && (
                            <p className="text-sm text-gray-500">
                              Phone: {invigilator.phone}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Invigilator
                          </span>
                          {invigilator.createdAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              Added: {formatDateTime(invigilator.createdAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <FaUsers className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Invigilators Assigned
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    No invigilators have been assigned to this assessment yet.
                    You can assign invigilators using the "Create Invigilators"
                    button.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsInvigilatorsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentCandidates;
